import React from 'react';
import {
  LocalizedMessage,
  LocalizedStandardDateNoSeconds,
} from 'intl/LocalizedComponents';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import _size from 'lodash/size';
import _toString from 'lodash/toString';
import PropTypes from 'prop-types';
// Core components
import Box from 'components/core/Box';
import Card from 'components/core/Card';
import Divider from 'components/core/Divider';
import List from 'components/core/List';
import Typography from 'components/core/Typography';
// Models
import mCarpark from 'models/cpm/carpark';
import mParking from 'models/cpm/parking';
import mPlanning from 'models/cpm/planning';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// HOCs
import withPermissions from 'hocs/WithPermissions';
// Custom
import {
  cpmParkingDataShape,
  cpmPlanningDataShape,
  cpmDoorDataShape,
} from 'constants/PropTypesShapes';
import { dateGenerator } from 'utils/DateUtils';
import InformationItem from 'components/items/InformationItem';
import {
  DateBeginItem,
  DateEndItem,
  EmailItem,
  PhoneNumberItem,
  SpacesQuantityItem,
  CommentsItem,
  PlanningItem,
  InfoItem,
  ParkingTypeItem,
  DateCreationItem,
} from 'components/items/InformationItems';
import AppIcon from 'components/icons/AppIcon';
import Callout from 'components/misc/Callout';
import PlanningInfoButton from 'cpmCarparks/assets/plannings/PlanningInfoButton';
import CarparkDoorItem from 'cpmCarparks/assets/main/CarparkDoorItem';
import CustomerItem from 'cpmCarparks/assets/customers/CustomerItem';

const ParkingInformation = ({
  parking,
  plannings,
  timezone,
  doors,
}) => {
  const {
    customer,
    begin,
    end,
    spacesQuantity,
    comments,
    creationDate,
    keypadCodes,
    vehicles,
    doors: parkingDoors,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    restrictedByForbiddenEntry,
    restrictedByPermanentExit,
    restrictedByForbiddenExit,
  } = parking;

  // Get parking status
  const {
    icon: statusIcon,
    color: statusColor,
    value: statusValue,
  } = mParking.status(parking);

  // Plannings
  const { parkingPlanning, allotmentPlanning } = plannings;

  // Doors
  const doorsIds = _map(parkingDoors, 'id');

  // Permissions
  const {
    pCpm: { planningRead },
  } = permissions;

  /* Banners forbidden entry and/or exit */
  /* =================================== */
  const entryAndExitForbidden = parking.restrictedByForbiddenEntry && parking.restrictedByForbiddenExit;

  const BannerEntryAndExitForbidden = entryAndExitForbidden ? (
    <div className="col-12 pt-3">
      <Callout withInfoIcon severity="warning">
        <LocalizedMessage id="info.entryAndExitForbiddenAccessRight" />
      </Callout>
    </div>
  ) : null;

  const BannerForbiddenEntry = (!entryAndExitForbidden && parking.restrictedByForbiddenEntry) ? (
    <div className="col-12 pt-3">
      <Callout withInfoIcon severity="warning">
        <LocalizedMessage id="info.entryForbiddenAccessRight" />
      </Callout>
    </div>
  ) : null;

  const BannerForbiddenExit = (!entryAndExitForbidden && parking.restrictedByForbiddenExit) ? (
    <div className="col-12 pt-3">
      <Callout withInfoIcon severity="warning">
        <LocalizedMessage id="info.exitForbiddenAccessRight" />
      </Callout>
    </div>
  ) : null;

  /* General information's */
  /* ===================== */

  /* CreationDate */
  const CreationDate = creationDate ? (
    <React.Fragment>
      <DateCreationItem
        light
        value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(creationDate, timezone)} />}
      />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Access right */
  const AccessRight = (
    <React.Fragment>
      <InformationItem
        light
        icon={statusIcon}
        iconColor={statusColor}
        value={(<LocalizedMessage id="info.parkingStatusDesc" values={{ status: statusValue }} />)}
      />
      <Divider variant="middle" />
    </React.Fragment>
  );

  /* Customer name */
  const CustomerName = customer ? (
    <React.Fragment>
      <CustomerItem light customer={customer} />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Customer email */
  const CustomerEmail = (customer && customer.email) ? (
    <React.Fragment>
      <EmailItem light value={customer.email} />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Customer phone */
  const CustomerPhone = (customer && customer.phone) ? (
    <React.Fragment>
      <PhoneNumberItem light value={customer.phone} />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Spaces quantity */
  const SpacesQuantity = <SpacesQuantityItem light value={<LocalizedMessage id="info.spacesQuantity" values={{ quantity: _toString(spacesQuantity) }} />} />;

  /* Begin */
  const Begin = begin ? (
    <React.Fragment>
      <Divider variant="middle" />
      <DateBeginItem
        light
        value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(begin, timezone)} />}
      />
    </React.Fragment>
  ) : null;

  /* End */
  const End = end ? (
    <React.Fragment>
      <Divider variant="middle" />
      <DateEndItem
        light
        value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(end, timezone)} />}
      />
    </React.Fragment>
  ) : null;

  /* Comments */
  const Comments = comments ? (
    <React.Fragment>
      <Divider variant="middle" />
      <CommentsItem light value={comments} />
    </React.Fragment>
  ) : null;

  /* AssignedSpace */
  const AssignedSpace = mParking.isAssigned(parking) ? (
    <React.Fragment>
      <Divider variant="middle" />
      <ParkingTypeItem light value={<LocalizedMessage id={mParking.type(parking).labelId} />} />
    </React.Fragment>
  ) : null;

  const GeneralInformations = (
    <Card
      title={<Typography type="subtitle2"><LocalizedMessage id="title.generalInfo" /></Typography>}
      contentOptions={{ expandable: true, expanded: true }}
    >
      <List disablePadding>
        {CreationDate}
        {AccessRight}
        {CustomerName}
        {CustomerEmail}
        {CustomerPhone}
        {SpacesQuantity}
        {Begin}
        {End}
        {Comments}
        {AssignedSpace}
      </List>
    </Card>
  );

  /* Carpark access ways */
  /* =================== */

  /* Keypad codes */
  const KeypadCodes = (
    <div>
      {_map(keypadCodes, (code, index) => (
        <React.Fragment key={index}>
          <InformationItem light icon="cAwTypeKCode" value={code} />
          {index < _size(keypadCodes) - 1 && <Divider variant="middle" />}
        </React.Fragment>
      ))}
    </div>
  );

  /* Vehicles */
  const Vehicles = (
    <div>
      {_map(vehicles, ({ licensePlate }, index) => (
        <React.Fragment key={index}>
          <InformationItem light icon="cAwTypeLPlate" value={licensePlate} />
          {index < _size(vehicles) - 1 && <Divider variant="middle" />}
        </React.Fragment>
      ))}
    </div>
  );

  const DividerKeypadVehicles = (KeypadCodes.length > 0 && Vehicles.length > 0) ? <div className="my-2" /> : null;

  const CarparkAccessWays = (
    <Card
      title={(<Typography type="subtitle2"><LocalizedMessage id="title.accessWaysCarpark" /></Typography>)}
      contentOptions={{
        expandable: true,
        expanded: true,
      }}
    >
      <List disablePadding>
        {KeypadCodes}
        {DividerKeypadVehicles}
        {Vehicles}
      </List>
    </Card>
  );

  /* Configuration */
  /* ============ */

  /* AntiPassback */
  const AntiPassback = (
    <React.Fragment>
      <div className="col-12">
        <Typography type="subtitle">
          <LocalizedMessage id="field.antipassback" />
        </Typography>
      </div>
      <div className="col-12 pt-1">
        {mParking.hasAntipassbackRestrictions(parking) ? (
          <List disablePadding>
            <InfoItem
              light
              checked={restrictedByAntipassbackEntrance}
              value={<LocalizedMessage id="field.restrictedEntrance" />}
            />
            <InfoItem
              light
              checked={restrictedByAntipassbackExit}
              value={<LocalizedMessage id="field.restrictedExit" />}
            />
          </List>
        ) : (
          <InformationItem
            light
            value={(
              <em>
                <LocalizedMessage id="info.notAppliedToAccessRight" />
              </em>
          )}
          />
        )}
      </div>
    </React.Fragment>
  );

  /* AccessRestrictionPlanning */
  const AccessRestrictionPlanning = (
    <OnlyWith
      permissions={{
        needed: planningRead,
        permissionsOrContext: 'carparkPermissions',
      }}
    >
      <React.Fragment>
        <div className="col-12 pt-3">
          <Typography type="subtitle">
            <LocalizedMessage id="field.accessRestrictionPlanning" />
          </Typography>
        </div>
        <div className="col-12 pt-1">
          {allotmentPlanning || parkingPlanning ? (
            <List disablePadding>
              {allotmentPlanning && (
              <React.Fragment>
                <PlanningItem
                  light
                  value={mPlanning.title(allotmentPlanning)}
                  rightIcon={<PlanningInfoButton planningId={allotmentPlanning.id} />}
                />
                {parkingPlanning && <Divider variant="middle" />}
              </React.Fragment>
              )}
              {parkingPlanning && (
              <PlanningItem
                light
                value={mPlanning.title(parkingPlanning)}
                rightIcon={<PlanningInfoButton planningId={parkingPlanning.id} />}
              />
              )}
            </List>
          ) : (
            <InformationItem
              light
              value={(
                <em>
                  <LocalizedMessage id="info.accessRightNoPlanning" />
                </em>
      )}
            />
          )}
        </div>
      </React.Fragment>
    </OnlyWith>
  );

  /* AccessAuthorized */
  const AccessAuthorized = (
    <React.Fragment>
      <div className="col-12 pt-3">
        <Typography type="subtitle">
          <LocalizedMessage id="field.doorsAuthorized" />
        </Typography>
      </div>
      <div className="col-12 pt-1">
        <List disablePadding>
          {_map(mCarpark.sortDoors(doors), (door) => (
            <CarparkDoorItem
              key={door.id}
              light
              door={door}
              rightIcon={
                <AppIcon defaultColor id={_includes(doorsIds, door.id) ? 'checkActive' : 'checkInactive'} />
    }
            />
          ))}
        </List>
      </div>
    </React.Fragment>
  );

  /* Options */
  const Options = (
    <React.Fragment>
      <div className="col-12 pt-3">
        <Typography type="subtitle">
          <LocalizedMessage id="field.options" />
        </Typography>
      </div>
      <div className="col-12 pt-1">
        <List disablePadding>
          <InfoItem
            light
            checked={restrictedByPermanentExit}
            value={<LocalizedMessage id="field.restrictedByPermanentExit" />}
          />
          <InfoItem
            light
            checked={restrictedByForbiddenEntry}
            value={<LocalizedMessage id="field.restrictedByForbiddenEntry" />}
          />
          <InfoItem
            light
            checked={restrictedByForbiddenExit}
            value={<LocalizedMessage id="field.restrictedByForbiddenExit" />}
          />
        </List>
      </div>
    </React.Fragment>
  );

  const Configuration = (
    <Card
      title={(<Typography type="subtitle2"><LocalizedMessage id="title.configuration" /></Typography>)}
      contentOptions={{ expandable: true, expanded: true }}
    >
      <div className="row">
        {AntiPassback}
        {AccessRestrictionPlanning}
        {AccessAuthorized}
        {Options}
      </div>
    </Card>
  );

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */

  return (
    <Box type="defaultBackground" className="p-3">
      <div className="row information-item-wrapper">
        {BannerEntryAndExitForbidden}
        {BannerForbiddenEntry}
        {BannerForbiddenExit}
        <div className="col-12 col-lg-6">
          <div className="mt-3">
            {GeneralInformations}
          </div>
          <div className="mt-3">
            {CarparkAccessWays}
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="mt-3">
            {Configuration}
          </div>
        </div>
      </div>
    </Box>
  );
};

/* ========================================================= */
/* PROPS                                                     */
/* ========================================================= */

ParkingInformation.propTypes = {
  parking: cpmParkingDataShape.isRequired,
  plannings: PropTypes.shape({
    allotmentPlanning: cpmPlanningDataShape,
    parkingPlanning: cpmPlanningDataShape,
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  doors: PropTypes.arrayOf(cpmDoorDataShape).isRequired,
};

/* ========================================================= */
/* EXPORT                                                    */
/* ========================================================= */

module.exports = withPermissions(ParkingInformation);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/parkings/ParkingInfo.js